import { EVENT_CATEGORY_LABELS, EVENT_SOURCE_LABELS } from './constants/constants';

export const LEGACY_ONE_TIME_TOTAL_TOOLTIP =
	'One-time quote total at time of event';

export const formatActedAsDisplayName = (row) => {
	const name = `${row?.FirstName || ''} ${row?.LastName || ''}`.trim();
	return name || row?.UserId || '';
};

/**
 * User column: actual actor who performed the action.
 * Legacy rows store actor username in ImpersonatedByUsername when impersonating.
 */
export const formatActorDisplayName = (row) => {
	if (row?.ActorDisplayName) {
		return row.ActorDisplayName;
	}

	if (row?.ImpersonatedByUsername) {
		return row.ImpersonatedByUsername;
	}

	const name = `${row?.FirstName || ''} ${row?.LastName || ''}`.trim();
	return name || row?.UserId || '';
};

export const isImpersonatedSessionRow = (row) => Boolean(row?.ImpersonatedByUsername);

const GROUPED_EVENT_DISPLAY_BY_CATEGORY = {
	Save: (count) => `${count} quote updates`,
	GridEdit: (count) => `${count} grid edits`,
	ItemAdded: (count) => `${count} items added`,
	ItemDeleted: (count) => `${count} items deleted`,
	TabAdded: (count) => `${count} tabs added`,
	TabDeleted: (count) => `${count} tabs deleted`,
	TabRestored: (count) => `${count} tabs restored`,
	TabUpdated: (count) => `${count} tab updates`,
	CrmSync: (count) => `${count} CRM sync events`,
	General: (count) => `${count} general events`
};

export const getEventCategoryDisplay = (row) => {
	const category = row?.EventCategory ?? row?.event_category;

	if (!category) {
		return '';
	}

	return EVENT_CATEGORY_LABELS[category] ?? category;
};

export const getGroupedEventDisplayText = (row) => {
	if (!row?.IsGrouped || !row?.GroupedCount) {
		return getEventDisplayText(row);
	}

	const category = row?.EventCategory ?? row?.event_category;
	const formatGrouped = GROUPED_EVENT_DISPLAY_BY_CATEGORY[category];

	if (formatGrouped) {
		return formatGrouped(row.GroupedCount);
	}

	return `${row.GroupedCount} similar events`;
};

export const parseAuditTrailChildRow = (child) => {
	if (!child) {
		return null;
	}

	if (typeof child === 'string') {
		try {
			return JSON.parse(child);
		} catch {
			return null;
		}
	}

	return child;
};

export const getRowEventInfo = (row) => row?.EventInfo ?? row?.event_info ?? '';

export const getRowContentText = (row) => row?.ContentText ?? row?.content_text ?? '';

export const getRowEventSource = (row) => row?.EventSource ?? row?.event_source ?? '';

const getRowCombinedMessage = (row) => {
	const eventInfo = getRowEventInfo(row);

	if (eventInfo.trim()) {
		return eventInfo;
	}

	return getRowContentText(row);
};

const parseBracketSourceFromMessage = (message) => {
	const match = /^\[([^\]]+)\]\s*([\s\S]*)$/.exec(message ?? '');

	if (!match) {
		return { source: '', message: message ?? '' };
	}

	const remainder = match[2] ?? '';

	return {
		source: match[1].trim(),
		message: remainder.trim() ? remainder : message
	};
};

export const stripBracketSourcePrefix = (message) =>
	parseBracketSourceFromMessage(message).message || message || '';

export const getGroupedChildEventText = (child) => {
	const row = parseAuditTrailChildRow(child);

	if (!row) {
		return '';
	}

	return stripBracketSourcePrefix(getRowEventInfo(row) || getRowContentText(row));
};

export const getEventDisplayText = (row) => {
	if (row?.IsGrouped && row?.GroupedCount > 1) {
		return getGroupedEventDisplayText(row);
	}

	const parsedRow = parseAuditTrailChildRow(row);
	const target = parsedRow ?? row;
	const fromFields = getRowEventInfo(target) || getRowContentText(target);

	if (fromFields) {
		return stripBracketSourcePrefix(fromFields);
	}

	return stripBracketSourcePrefix(getRowCombinedMessage(row));
};

export const getEventSourceDisplay = (row) => {
	const stored = getRowEventSource(row);
	const resolved = stored
		? stored
		: parseBracketSourceFromMessage(getRowCombinedMessage(row)).source;

	if (!resolved) {
		return '';
	}

	return EVENT_SOURCE_LABELS[resolved] ?? resolved;
};

export const formatEventDateCell = (value) => {
	if (!value) {
		return { raw: null, display: '' };
	}

	const date = new Date(value);

	if (Number.isNaN(date.getTime())) {
		return { raw: null, display: String(value) };
	}

	return {
		raw: date,
		display: date.toLocaleString(undefined, {
			year: 'numeric',
			month: 'numeric',
			day: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
			second: '2-digit'
		})
	};
};

const isLegacyUnsetTotalContractValue = (row, totalContractValue) => {
	if (totalContractValue === null || totalContractValue === undefined || totalContractValue === '') {
		return true;
	}

	if (Number(totalContractValue) !== 0) {
		return false;
	}

	// NULL total_contract_value is read as 0 for legacy rows (no category/source).
	return !row?.EventCategory && !row?.event_category;
};

export const getQuoteTotalDisplay = (row, formatCurrency) => {
	const quoteTotal = row?.QuoteTotal ?? row?.quote_total;
	const totalContractValue = row?.TotalContractValue ?? row?.total_contract_value;

	const hasQuoteTotal = quoteTotal !== null && quoteTotal !== undefined && quoteTotal !== '';
	const hasTcv = !isLegacyUnsetTotalContractValue(row, totalContractValue);

	// NULL/unset total_contract_value is read as 0 for legacy rows — prefer quote_total when present.
	const useTcv = hasTcv && !(hasQuoteTotal && Number(totalContractValue) === 0);

	if (useTcv) {
		return {
			value: formatCurrency ? formatCurrency(totalContractValue) : String(totalContractValue),
			isLegacyOneTimeOnly: false
		};
	}

	if (!hasQuoteTotal) {
		return { value: '', isLegacyOneTimeOnly: false };
	}

	return {
		value: formatCurrency ? formatCurrency(quoteTotal) : String(quoteTotal),
		isLegacyOneTimeOnly: true,
		tooltip: LEGACY_ONE_TIME_TOTAL_TOOLTIP
	};
};

export const formatQuoteVersionDisplay = (row) => {
	const version = row?.QuoteVersion;
	if (version === null || version === undefined || version === '') {
		return '';
	}

	return String(version);
};

export const auditLogGridToggleButtonSx = {
	minWidth: '28px !important',
	minHeight: '28px !important',
	width: 28,
	height: 28,
	marginTop: '0 !important',
	padding: '4px !important',
	'&:hover': {
		backgroundColor: 'transparent'
	}
};
